import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { HubBrief, HubListFacade, HubListSearchCriteria, LoadingFacade } from '../../../store';
import {
  ListQueryParam,
  ListRange,
  ListServerPagination,
  ListServerPaginationStateModel,
  unchangedForm,
} from '../../../common';
import { ButtonAction, ButtonType } from '../../../ui-kit/action-button';
import { FilterSearch, FilterSettings } from '../../../ui-kit/list-filter';
import { TranslateService } from '@ngx-translate/core';
import { HubSelectionItem } from '../models';

@Component({
    selector: 'lib-hubs-selection-dialog',
    templateUrl: './hubs-selection-dialog.component.html',
    styleUrls: ['./hubs-selection-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class HubsSelectionDialogComponent
  extends ListServerPagination<HubBrief>
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(MatPaginator) paginator: MatPaginator;

  displayedColumns = ['select', 'name', 'ssccId'];
  listDto$: Observable<ListServerPaginationStateModel<HubBrief>>;
  filterConfig: FilterSettings;
  searchCriteriaValue: HubListSearchCriteria = new HubListSearchCriteria();
  buttonAction = ButtonAction;
  buttonType = ButtonType;
  selectedRows = new Map<number, HubBrief>();
  hasValue$ = new BehaviorSubject<boolean>(false);
  deletedIds: number[] = [];
  initialHubList: number[] = [];
  isFormUnchanged: (initialHubList: number[], updatedHubList: number[]) => boolean = unchangedForm;

  constructor(
    translateService: TranslateService,
    public dialogRef: MatDialogRef<HubsSelectionDialogComponent>,
    public hubListFacade: HubListFacade,
    public loadingFacade: LoadingFacade,
    @Inject(MAT_DIALOG_DATA) public data: HubSelectionItem[],
  ) {
    super(translateService);
    this.initialHubList = data.map((hub: HubSelectionItem) => hub.id);
    this.listDto$ = hubListFacade.listDto$;
  }

  ngOnInit(): void {
    this.initFiltersForm();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    setTimeout(() => this.fetchListBySearchCriteria(), 0);
  }

  ngOnDestroy(): void {
    this.hubListFacade.setInitialState();
  }

  fetchList(listRange: ListRange): void {
    timer(0)
      .pipe(take(1))
      .subscribe(() => {
        this.searchCriteriaValue = {
          ...this.searchCriteriaValue,
          ...listRange,
        };
        this.hubListFacade.loadListForDialog(new HubListSearchCriteria(this.searchCriteriaValue));
      });
  }

  fetchListBySearchCriteria(filterValue?: HubListSearchCriteria): void {
    this.searchCriteriaValue = {
      ...this.searchCriteriaValue,
      ...filterValue,
      page: 0,
    };
    this.hubListFacade.loadListForDialog(new HubListSearchCriteria(this.searchCriteriaValue));
  }

  noHubsExist(): boolean {
    return !this.searchCriteriaValue[ListQueryParam.Filter] && this.dataSource && this.dataSource.data.length === 0;
  }

  confirm(): void {
    this.dialogRef.close({ selected: [...this.selectedRows.values()], deleted: [...this.deletedIds] });
    this.deletedIds = [];
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  isAllSelected(): boolean {
    return this.dataSource.data.every((row: HubBrief) => this.selectedRows.has(row.id));
  }

  masterToggle(shouldAdd: boolean): void {
    this.dataSource.data.forEach((row: HubBrief) => this.updateSelectedHubs(row, shouldAdd));
    this.hasValue$.next(this.hasValue());
  }

  toggle(row: HubBrief, shouldAdd: boolean): void {
    this.updateSelectedHubs(row, shouldAdd);
    this.hasValue$.next(this.hasValue());
  }

  updatedHubList(): number[] {
    return [...this.selectedRows.values()].map((hub: HubBrief) => hub.id);
  }

  protected updateTable(tableDto: ListServerPaginationStateModel<HubBrief>): void {
    super.updateTable(tableDto);

    const hubSelectionIds: number[] = (this.data || []).map((hub: HubSelectionItem) => hub.id);

    this.dataSource.data
      .filter((row: HubBrief) => hubSelectionIds.includes(row.id))
      .forEach((row: HubBrief) => this.updateSelectedHubs(row));

    this.hasValue$.next(this.hasValue());
  }

  private initFiltersForm(): void {
    this.filterConfig = {
      items: [new FilterSearch({ name: ListQueryParam.Filter })],
    };
  }

  private updateSelectedHubs(row: HubBrief, shouldAdd: boolean = true): void {
    shouldAdd ? this.addData(row) : this.deleteData(row);
  }

  private addData(row: HubBrief): void {
    this.selectedRows.set(row.id, row);
    this.deletedIds = this.deletedIds.filter((item: number) => item !== row.id);
  }

  private deleteData(row: HubBrief): void {
    this.selectedRows.delete(row.id);
    this.deletedIds.push(row.id);
  }

  private hasValue(): boolean {
    return this.dataSource.data.some((row: HubBrief) => this.selectedRows.has(row.id));
  }
}
